import React, { useEffect, useState } from "react";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import Pagination from "../../../component/library/pagination";
import RestrictedWordsForm from "./form";
import ExcelTable from "../../../component/library/excelTable";
import { FaSync } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import { createRestrictedWord, fetchRestrictedWords, hardDeleteRestrictedWord, softDeleteRestrictedWord, updateRestrictedWord } from "../../../service/admin/restrictedWord";
import { MdDelete, MdModeEdit, MdDeleteForever } from "react-icons/md";

export default function RestrictedWords() {
    const limit = 50;
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedWord, setSelectedWord] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [word, setWord] = useState('');
    const [isUpdate, setIsUpdate] = useState(false);

    const loader = useLoader();
    const message = useMessage();

    const authData = getAuthLocalStorage();
    const currentUserId = authData?.loginHeader || null;

    const fetchAllRestrictedWords = async () => {
        setLoading(true);
        try {
            const response = await fetchRestrictedWords(page, limit);
            if (response?.data?.data?.length > 0) {
                const transformedData = response.data.data.map((item, index) => ({
                    id: item._id,
                    serialNumber: (page - 1) * limit + index + 1,
                    word: item.name,
                    createdByName: `${item.createdBy?.firstName || ''}${item.createdBy?.lastName ? ` ${item.createdBy?.lastName}` : ''}`.trim()
                }));
                setData(transformedData);
                setTotal(response.data.total);
                setPages({ currentPage: response.data.page, totalPages: response.data.totalPages, totalItems: response.data.total });
            } else {
                setData([]);
                setTotal(0);
                setPages({ currentPage: 1, totalPages: 1, totalItems: 0 });
            }
        } catch (error) {
            message.showToast("Failed to fetch restricted words", ToastTypes.ERROR);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        fetchAllRestrictedWords();
    }, [])

    useEffect(() => {
        fetchAllRestrictedWords();
    }, [page, limit]);

    const handleHardDelete = async wordId => {
        try {
            setLoading(true);
            const response = await hardDeleteRestrictedWord(wordId);
            if (response && (response?.status?.code == 200)) {
                message.showToast("Restricted word deleted successfully", ToastTypes.SUCCESS);
                fetchAllRestrictedWords();
            }
        } catch (error) {
            message.showToast("Failed to delete restricted word", ToastTypes.ERROR);
        } finally {
            setLoading(false);
        }
    }

    const handleSoftDelete = async wordId => {
        try {
            setLoading(true);
            const response = await softDeleteRestrictedWord(wordId);
            if (response && (response?.status?.code == 200)) {
                message.showToast("Restricted word soft deleted successfully", ToastTypes.SUCCESS);
                fetchAllRestrictedWords();
            }
        } catch (error) {
            message.showToast("Failed to soft delete restricted word", ToastTypes.ERROR);
        } finally {
            setLoading(false);
        }
    }

    const handleEdit = wordId => {
        const wordToEdit = data.find(item => item.id === wordId);
        if (wordToEdit) {
            setSelectedWord(wordToEdit);
            setWord(wordToEdit.word);
            setIsUpdateMode(true);
            setShowCreateForm(true);
        }
    }

    const columns = [
        {
            key: "serialNumber", label: "S.No.", width: 40,
            render: (value) => <span className="rm-serial">{value}</span>
        },
        {
            key: "word", label: "Restricted Word", width: 300,
            render: (value) => <span className="rm-word">{value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()}</span>
        },
        {
            key: "createdByName", label: "Created By", width: 200,
            render: (value) => <span>{value}</span>
        },
        {
            key: "actions", label: "Actions", width: 100,
            render: (_, row) => (
                <div className="pd-actions-cell">
                    <button className="pd-action-btn pd-action-edit" onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }} title="Edit"><MdModeEdit /></button>
                    <button className="pd-action-btn pd-action-delete" onClick={(e) => { e.stopPropagation(); handleHardDelete(row.id); }} title="Hard Delete"><MdDeleteForever /></button>
                    <button className="pd-action-btn pd-action-delete" onClick={(e) => { e.stopPropagation(); handleSoftDelete(row.id); }} title="Soft Delete"><MdDelete /></button>
                </div>
            )
        }
    ];

    const toolbarLeftActions = (
        <></>
    );

    const toolbarRightActions = (
        <div className="rm-toolbar-right">
            <button className="rm-refresh-btn" title="Refresh">
                <FaSync className={loading ? "rm-spin" : ""} />
            </button>
            <button className="rm-create-btn" onClick={() => setShowCreateForm(true)}>
                <FaPlus /> Insert Restricted Word
            </button>
        </div>
    );

    const handleSelectionChange = (selected) => {
        setSelectedWord(selected);
    };

    const handleCancel = () => {
        setShowCreateForm(false);
        setWord('');
        setIsUpdate(false);
    };

    const handleSave = async (ev) => {
        ev.preventDefault();
        try {
            let responseData;
            if (isUpdateMode) {
                responseData = await updateRestrictedWord(selectedWord.id, { word })
            } else {
                responseData = await createRestrictedWord({ word })
            }
            if (responseData && (responseData?.status?.code == 200)) {
                message.showToast("Restricted word saved successfully", ToastTypes.SUCCESS);
                setShowCreateForm(false);
                fetchAllRestrictedWords();
            } else {
                message.showToast("Failed to save restricted word", ToastTypes.ERROR);
            }
        } catch (error) {
            message.showToast("Error saving restricted word", ToastTypes.ERROR);
        }
    }

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    return (
        <>
            <div className="pd-page">
                <div className="pd-table-section">
                    <ExcelTable
                        data={data}
                        columns={columns}
                        isLoading={initialLoading || loading}
                        loadingText="Loading restricted words..."
                        showToolbarExport={false}
                        showToolbarFilter={false}
                        showToolbarColumns={true}
                        showToolbarFullscreen={true}
                        toolbarLeftActions={toolbarLeftActions}
                        toolbarRightActions={toolbarRightActions}
                        onSelectionChange={handleSelectionChange}
                        selectedData={selectedWord}
                        selectable={false}
                        sortable={true}
                        resizable={true}
                        showToolbar={true}
                        title="Restricted Words"
                        // onRowClick={openDetailPage}
                        userId={currentUserId}
                    />

                    <div className="pd-pagination">
                        <div className="pd-pagination-info">Showing {startItem} to {endItem} of {pages.totalItems} drafts</div>
                        <div className="pd-pagination-controls">
                            <button className="pd-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
                            {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                                let pageNum;
                                if (pages.totalPages <= 5) pageNum = i + 1;
                                else if (page <= 3) pageNum = i + 1;
                                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                                else pageNum = page - 2 + i;
                                return (
                                    <button key={pageNum} className={`pd-page-btn ${page === pageNum ? "pd-page-btn-active" : ""}`} onClick={() => setPage(pageNum)}>{pageNum}</button>
                                );
                            })}
                            {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                                <>
                                    <span className="pd-page-dots">...</span>
                                    <button className="pd-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                                </>
                            )}
                            <button className="pd-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
                        </div>
                    </div>
                </div>
            </div>

            <RestrictedWordsForm
                show={showCreateForm}
                onClose={() => setShowCreateForm(false)}
                selectedWord={selectedWord}
                refreshList={fetchAllRestrictedWords}
                handleSave={handleSave}
                word={word}
                setWord={setWord}
                handleCancel={handleCancel}
            />
        </>
    )
}
